import moment from 'moment-timezone';

import { NullableString } from './types';

/**
 * Converts a time string in "HH:mm" format into a "fake float" number format.
 * - "09:30" becomes 9.30 (NOT 9.5)
 * - "14:45" becomes 14.45 (NOT 14.75)
 * @param time - A string representing time in "HH:mm" format
 * @returns A number like 9.30 or 14.45, or -1 if input is falsy
 */
export function parseTimeToFloat(time: string): number {
  if (!time) return -1;

  const [ hoursStr, minutesStr = '0' ] = time.split(':');
  const hours = Number(hoursStr);
  const minutes = Number(minutesStr);

  if (isNaN(hours) || isNaN(minutes)) {
    return NaN;
  }

  return Number(`${hours}.${minutes.toString().padStart(2, '0')}`);
}

const padTo = (value: number, length: number) => value.toString().padStart(length, '0');

/**
 * Formats a `Date` as `HH:mm:ss.SSS` using native getters.
 * Equivalent to `moment(date).format('HH:mm:ss.SSS')` (or `moment.utc(date)...` when `isUtc`), but
 * without constructing a moment - this runs once per quote on every market-data frame.
 * @param date - the date to format
 * @param isUtc - read UTC components instead of local ones
 */
export function formatTimeOfDay(date: Date, isUtc = false): string {
  const hours = isUtc ? date.getUTCHours() : date.getHours();
  const minutes = isUtc ? date.getUTCMinutes() : date.getMinutes();
  const seconds = isUtc ? date.getUTCSeconds() : date.getSeconds();
  const milliseconds = isUtc ? date.getUTCMilliseconds() : date.getMilliseconds();

  return `${padTo(hours, 2)}:${padTo(minutes, 2)}:${padTo(seconds, 2)}.${padTo(milliseconds, 3)}`;
}

/**
 * Returns true if date is Saturday (weekday=6) or Sunday (weekday=7) in America/New_York timezone.
 * @param dateInNYTime - a moment object or date string
 * @returns true if Saturday or Sunday, null for invalid input
 */
export function isDateMarketSaturdayOrSunday(
  dateInNYTime: moment.Moment | NullableString,
): boolean | null {
  if (!dateInNYTime) {
    console.error('[DateTimeUtils] isDateMarketSaturdayOrSunday - invalid input date');
    return null;
  }
  let date: moment.Moment;
  if (moment.isMoment(dateInNYTime)) {
    date = dateInNYTime;
  } else if (typeof dateInNYTime === 'string') {
    // parse date string (take YYYY-MM-DD part)
    const datePart = dateInNYTime.substr(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      console.error(`[DateTimeUtils] isDateMarketSaturdayOrSunday - invalid date string '${dateInNYTime}'`);
      return null;
    }
    date = moment.tz(datePart, 'America/New_York');
  } else {
    console.error('[DateTimeUtils] isDateMarketSaturdayOrSunday - invalid input type');
    return null;
  }
  if (!date.isValid()) {
    console.error(`[DateTimeUtils] isDateMarketSaturdayOrSunday - parsed moment is invalid for '${dateInNYTime}'`);
    return null;
  }
  // isoWeekday: 1 (Mon) ... 7 (Sun)
  const weekday = date.isoWeekday();
  return weekday === 6 || weekday === 7;
}
